import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useCallback, useContext } from 'react';
import { httpWithAuth } from '~/clients';
import { PRICING } from '~/constants/endpoints';
import { useConfiguration } from '~/context/common/StaticContext';
import TradeContext, {
  useGetAcquisitionNameForApiRequest,
  useIsTradeInApplied,
  useSelectedTrade
} from '~/context/common/TradeContext';
import { useUserLocation } from '~/context/common/UserContext';
import AvailabilityAndPriceContext from '~/context/home/AvailabilityAndPriceContext';
import FinanceContext from '~/context/home/FinanceContext';
import type { PaymentTerm } from '~/contracts/common';
import type { PricingResponse } from '~/services/common/contracts';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';

export function chunkNumberArray(array: number[], chunkSize: number) {
  const chunks: number[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }

  return chunks;
}

function useChunkedPricing() {
  const { zip } = useUserLocation();
  const applyTradeIn = useIsTradeInApplied();
  const preferredAcquisitionName = useGetAcquisitionNameForApiRequest();
  const selectedTrade = useSelectedTrade();
  const { softPullCompletedTrigger } = useContext(FinanceContext);

  return useCallback(
    async (controller: AbortController, vehicleIdSets: number[][]) => {
      const fetchPromises = vehicleIdSets.map(chunk =>
        httpWithAuth
          .post<PricingResponse>(
            PRICING,
            {
              applyVerboseLogging: false,
              browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
              vehicleIds: chunk,
              zip5: zip,
              applyTradeIn,
              preferredAcquisitionName
            },
            { signal: controller.signal }
          )
          .then(({ data }) => data)
      );

      return Promise.allSettled(fetchPromises);
    },
    // selectedTrade forces a refetch on every trade change, since preferredAcquisitionName
    // stays '' and doesn't change when a shopper has only one trade; softPullCompletedTrigger reprices new terms.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      applyTradeIn,
      preferredAcquisitionName,
      selectedTrade,
      softPullCompletedTrigger,
      zip
    ]
  );
}

export default function useGetBulkPricing() {
  const {
    pageVehicleIds,
    pagePricing: { setData, setError, setLoading, setLoaded },
    tradeInfo: { setData: setTradeInfo }
  } = useContext(AvailabilityAndPriceContext);
  const { setTradeInApplied } = useContext(TradeContext);
  const chunkedFetchPricing = useChunkedPricing();
  const { PricingConfig } = useConfiguration();
  //default to lower value if accidentally deleted from configuration
  const chunkSize = PricingConfig ? PricingConfig.MaxVehiclesToPrice : 50;

  useEffectWithController(
    controller => {
      if (!pageVehicleIds) return;
      if (pageVehicleIds.length === 0) {
        setLoaded();

        return;
      }

      setLoading();

      chunkedFetchPricing(
        controller,
        chunkNumberArray(pageVehicleIds, chunkSize)
      )
        .then(results => {
          const successfulResults = results
            .filter(
              (result): result is PromiseFulfilledResult<PricingResponse> =>
                result.status === 'fulfilled'
            )
            .map(result => result.value);

          const combinedVehiclePaymentTermsMapping = successfulResults.reduce<
            Record<number, PaymentTerm>
          >((accumulator, result) => {
            return {
              ...accumulator,
              ...result.vehiclePaymentTermsMapping
            };
          }, {});

          if (successfulResults[0] && successfulResults[0].tradeInfo) {
            const apiTradeInfo = successfulResults[0].tradeInfo;
            setTradeInfo(apiTradeInfo);

            if (apiTradeInfo.applyTradeIn != null) {
              setTradeInApplied(apiTradeInfo.applyTradeIn);
            }
          }

          setData(combinedVehiclePaymentTermsMapping);
        })
        .catch(
          generateErrorLogger(
            'getBulkPricing',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      chunkSize,
      chunkedFetchPricing,
      pageVehicleIds,
      setData,
      setError,
      setLoaded,
      setLoading,
      setTradeInApplied,
      setTradeInfo
    ]
  );
}
